import { useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { type Case, Role } from "@/briefpoint-client";
import Button from "@/components/Button";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import AddEditClientModal from "@/components/CaseManagement/AddEditClientModal";
import CaseTableMulti from "@/components/CaseManagement/CaseTableMulti";
import Loading from "@/components/Loading";
import { Modal } from "@/components/Modals";
import PageHeader from "@/components/PageHeader";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useCaseApi, useClientApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useFirm from "@/hooks/useFirm";
import type { CaseResponse, ClientResponse, UserResponse } from "@/lib/types";
import { getCaseSourceLogo } from "@/utils/ExternalPartner/utils";
import styles from "./client.module.scss";

type ClientParams = {
  id: string;
};

export default function ClientPageMulti() {
  const { user, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  const location = useLocation<{ client: ClientResponse }>();
  const history = useHistory();
  const { id } = useParams<ClientParams>();
  const clientApi = useClientApi();
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [cases, setCases] = useState<CaseResponse[] | undefined>();
  const [client, setClient] = useState<ClientResponse | undefined>(
    location.state?.client,
  );
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { jurisdictions } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setShowEditClient(params.get("editClient") === "true");
  }, [location.search]);

  const loadClient = useCallback(
    async (
      user: UserResponse | undefined,
      client: ClientResponse | undefined,
    ) => {
      if (user?.firmId && !client) {
        const client = (await clientApi.clientGet({
          firmId: user.firmId,
          id,
        })) as ClientResponse;
        setClient(client);
      }
    },
    [clientApi, id],
  );

  useEffect(() => {
    loadClient(user, client);
  }, [user, loadClient, client]);

  const loadCases = useCallback(
    async (user: UserResponse | undefined) => {
      if (user?.firmId && id) {
        const cases = (await caseApi.caseGetAll({
          firmId: user.firmId,
          clientId: id,
          usersCasesOnly: !user?.role?.includes(Role.FirmAdmin),
        })) as CaseResponse[];
        setCases(cases);
      }
    },
    [caseApi, id],
  );

  useEffect(() => {
    loadCases(user);
  }, [user, loadCases]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setTags(tagsList);
    }
    if (!tags) {
      loadTags();
    }
  }, [tagApi, tags]);

  async function AddCase(_case: Case) {
    const newCase = (await caseApi.casePost({
      firmId: user?.firmId || "",
      clientId: id,
      _case,
    })) as CaseResponse;

    if (newCase) {
      if (cases) {
        setCases((c) => [...(c ?? []), newCase]);
      } else {
        setCases([newCase]);
      }
    }

    closeAddCase();
  }

  function closeAddCase() {
    setShowAddCase(false);
  }

  async function handleClientDelete() {
    await clientApi.clientDelete({
      firmId: user?.firmId || "",
      id: client?.id ?? "",
    });

    history.push("/clients");
  }

  const firmHasOnlyOneJx = useMemo(() => {
    const uniqueJurisdictionIds = (firm.offices ?? []).reduce((acc, office) => {
      for (const jurisdiction of office.jurisdictions ?? []) {
        if (jurisdiction.jurisdictionId) {
          acc.add(jurisdiction.jurisdictionId);
        }
      }
      return acc;
    }, new Set<string>());
    return uniqueJurisdictionIds.size === 1;
  }, [firm.offices]);

  const filteredCases = cases?.filter((x) => {
    if (!search) return true;

    const split = (x.title || "").concat(x.shortTitle || "").split(" ");

    for (let i = 0; i < split?.length; i++) {
      if (split[i].toLowerCase().startsWith(search.toLowerCase())) {
        return true;
      }
    }

    return false;
  });

  return (
    <>
      <Container className={styles.pageContainerMulti}>
        <Loading
          isLoading={
            client === undefined &&
            cases === undefined &&
            jurisdictions === undefined &&
            users === undefined
          }
          className="mt-3"
        >
          <PageHeader
            title={
              <div className={styles.breadCrumbs}>
                <Link to={"/clients"}>Clients</Link> {`> ${client?.name}`}
              </div>
            }
            className="mt-0"
          />

          <div className={styles.clientNameHeader}>
            <p className={styles.clientNameText}>
              {getCaseSourceLogo(client?.integration?.partner)}
              <span className="ms-2">{client?.name}</span>
            </p>
          </div>

          <div className="d-flex mb-2 gap-2 align-items-center">
            <Button variant="outline" onClick={() => setShowEditClient(true)}>
              <PencilFill />
              &nbsp;Edit Client Details
            </Button>
            {cases && !cases.length && (
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <TrashFill />
                &nbsp;Delete Client
              </Button>
            )}
          </div>
          <div className={styles.separator} />

          <div className="d-flex justify-content-between mb-2 gap-2 align-items-center">
            <SearchInput
              value={search}
              placeholder="Search cases"
              onChange={(value) => setSearch(value.toLowerCase())}
              className="w-50"
            />

            <Button variant="default" onClick={() => setShowAddCase(true)}>
              Add New Case
            </Button>
          </div>

          <div className={styles.clientDetailsSection}>
            {(filteredCases?.length ?? 0) > 5 && (
              <div>
                <h2 style={{ margin: "0px", marginBottom: "8px" }}>
                  Recent Cases
                </h2>
                <CaseTableMulti
                  cases={filteredCases}
                  recent
                  addCase={() => setShowAddCase(true)}
                  users={users}
                  jurisdictions={jurisdictions}
                  firmHasOnlyOneJx={firmHasOnlyOneJx}
                />
                <span>&nbsp;&nbsp;</span>
                <h2 style={{ margin: "0px", marginBottom: "8px" }}>
                  All Cases
                </h2>
              </div>
            )}
            <CaseTableMulti
              cases={filteredCases}
              addCase={() => setShowAddCase(true)}
              users={users}
              jurisdictions={jurisdictions}
              firmHasOnlyOneJx={firmHasOnlyOneJx}
            />
          </div>
          {client && firm && (
            <>
              <AddEditClientModal
                title="Edit Client"
                submitButtonText="Save"
                isShow={showEditClient}
                onClose={() => setShowEditClient(false)}
                onClientSaved={setClient}
                client={client}
                users={users}
              />
              <AddEditCaseModal
                availableTags={tags}
                setAvailableTags={setTags}
                title="Add Case"
                confirm="Add"
                isShow={showAddCase}
                onClose={() => setShowAddCase(false)}
                onConfirm={AddCase}
                users={users}
                jurisdictions={jurisdictions ?? []}
                client={client}
              />
            </>
          )}
        </Loading>
      </Container>
      <Modal
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleClientDelete}
        show={showDeleteConfirm}
        confirmText="Yes, Delete"
        title="Delete Client"
      >
        Are you sure you want to delete this Client? This action cannot be
        undone.
      </Modal>
    </>
  );
}
